import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuickAuditForm = ({ isVisible, onClose }) => {
  const [formData, setFormData] = useState({
    website: '',
    email: '',
    businessType: '',
    primaryGoal: '',
    currentChallenges: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const businessTypeOptions = [
    { value: 'ecommerce', label: 'E-commerce Store' },
    { value: 'service', label: 'Service Business' },
    { value: 'saas', label: 'SaaS/Tech Company' },
    { value: 'restaurant', label: 'Restaurant/Food' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'nonprofit', label: 'Non-profit' },
    { value: 'other', label: 'Other' }
  ];

  const goalOptions = [
    { value: 'traffic', label: 'Increase Website Traffic' },
    { value: 'leads', label: 'Generate More Leads' },
    { value: 'sales', label: 'Boost Online Sales' },
    { value: 'engagement', label: 'Improve User Engagement' },
    { value: 'brand', label: 'Build Brand Awareness' },
    { value: 'conversion', label: 'Increase Conversion Rate' }
  ];

  const challengeOptions = [
    'Low website traffic',
    'Poor search rankings',
    'Low social media engagement',
    'High bounce rate',
    'Poor mobile experience',
    'Slow loading speed',
    'Outdated design',
    'No clear call-to-actions'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleChallengeToggle = (challenge) => {
    setFormData(prev => ({
      ...prev,
      currentChallenges: prev?.currentChallenges?.includes(challenge)
        ? prev?.currentChallenges?.filter(c => c !== challenge)
        : [...prev?.currentChallenges, challenge]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.website?.trim()) {
      newErrors.website = 'Website URL is required';
    } else if (!formData?.website?.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Please enter a valid URL (including http:// or https://)';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData?.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+₹/)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.businessType) {
      newErrors.businessType = 'Please select your business type';
    }
    
    if (!formData?.primaryGoal) {
      newErrors.primaryGoal = 'Please select your primary goal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Quick audit requested:', formData);
      
      // Show success message and close
      alert('Audit request submitted! You\'ll receive your report within 24 hours.');
      onClose();
    } catch (error) {
      console.error('Error submitting audit request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-primary">Quick Digital Audit</h3>
              <p className="text-muted-foreground">Get instant insights in 24 hours</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={24} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Website URL"
            type="url"
            placeholder="https://yourwebsite.com"
            value={formData?.website}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            error={errors?.website}
            required
            description="We'll analyze your website's performance, SEO, and user experience"
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
            description="Where should we send your audit report?"
          />

          <Select
            label="Business Type"
            placeholder="Select your business type"
            options={businessTypeOptions}
            value={formData?.businessType}
            onChange={(value) => handleInputChange('businessType', value)}
            error={errors?.businessType}
            required
          />

          <Select
            label="Primary Goal"
            placeholder="What's your main objective?"
            options={goalOptions}
            value={formData?.primaryGoal}
            onChange={(value) => handleInputChange('primaryGoal', value)}
            error={errors?.primaryGoal}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Current Challenges (Select all that apply)
            </label>
            <div className="grid grid-cols-1 gap-2">
              {challengeOptions?.map((challenge) => (
                <label
                  key={challenge}
                  className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer brand-transition ${
                    formData?.currentChallenges?.includes(challenge)
                      ? 'border-accent bg-accent/10 text-accent' :'border-border hover:border-accent/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData?.currentChallenges?.includes(challenge)}
                    onChange={() => handleChallengeToggle(challenge)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    formData?.currentChallenges?.includes(challenge)
                      ? 'border-accent bg-accent' :'border-muted-foreground'
                  }`}>
                    {formData?.currentChallenges?.includes(challenge) && (
                      <Icon name="Check" size={12} className="text-white" />
                    )}
                  </div>
                  <span className="text-sm">{challenge}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-trust/10 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-trust mt-0.5" />
              <div>
                <h4 className="font-semibold text-trust mb-1">What you'll receive:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Comprehensive website performance analysis</li>
                  <li>• SEO optimization recommendations</li>
                  <li>• Social media presence evaluation</li>
                  <li>• Actionable improvement suggestions</li>
                  <li>• Priority recommendations based on impact</li>
                </ul>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            fullWidth
            loading={isSubmitting}
            className="bg-trust hover:bg-trust/90 text-white"
            iconName="Zap"
            iconPosition="left"
            iconSize={18}
          >
            {isSubmitting ? 'Processing Audit...' : 'Get My Free Audit'}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            No spam, ever. We'll only send you the audit report and occasional valuable insights.
          </p>
        </form>
      </div>
    </div>
  );
};

export default QuickAuditForm;